/**
 * manager-cart.js
 * Gerencia operações de carrinho via Shopify Cart API.
 * Disponível como window.CartManager.
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   await window.CartManager.add(variantId, { quantity, properties, button, action })
 *   await window.CartManager.remove(lineItemKey, { button, action })
 *   await window.CartManager.update(lineItemKey, quantity, { button })
 *   await window.CartManager.get()
 *   await window.CartManager.clear()
 *
 *   window.CartManager.on('added', callback)    → retorna função de cleanup
 *   window.CartManager.off('added', callback)
 *
 * ─── Retorno dos métodos ──────────────────────────────────────────────────
 *
 *   { success: true,  data: {...} }
 *   { success: false, error: 'mensagem' }
 *
 * ─── Eventos DOM emitidos (CustomEvent em document) ───────────────────────
 *
 *   'cart:added'   → e.detail: { variantId, quantity, data, action }
 *   'cart:removed' → e.detail: { lineItemKey, data, action }
 *   'cart:updated' → e.detail: { lineItemKey, quantity, data }
 *   'cart:cleared' → e.detail: { data }
 *   'cart:error'   → e.detail: { action, error, variantId?, lineItemKey? }
 *
 * ─── Eventos internos (via .on()) ─────────────────────────────────────────
 *
 *   'added', 'removed', 'updated', 'cleared', 'error'
 *   (mesmos dados do CustomEvent)
 */

/* ─── CartAPI ─────────────────────────────────────────────────────────────── */

class CartAPI {
  constructor(timeout = 5000) {
    this._timeout = timeout;
  }

  async _fetch(url, options = {}) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), this._timeout);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${res.status}`);
      }
      return res.json();
    } finally {
      clearTimeout(timerId);
    }
  }

  add(variantId, quantity = 1, properties = {}) {
    return this._fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
  }

  update(lineItemKey, quantity) {
    return this._fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });
  }

  remove(lineItemKey) {
    return this.update(lineItemKey, 0);
  }

  get() {
    return this._fetch('/cart.js');
  }

  clear() {
    return this._fetch('/cart/clear.js', { method: 'POST' });
  }
}

/* ─── CartNotifier ────────────────────────────────────────────────────────── */

class CartNotifier {
  constructor() {
    this._listeners = {};
  }

  on(event, cb) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(cb);
    return () => this.off(event, cb);
  }

  off(event, cb) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter(fn => fn !== cb);
  }

  emit(event, data) {
    this._listeners[event]?.forEach(cb => cb(data));
    document.dispatchEvent(
      new CustomEvent(`cart:${event}`, { bubbles: true, detail: data })
    );
  }
}

/* ─── CartManager ─────────────────────────────────────────────────────────── */

class CartManager {
  constructor() {
    this._api      = new CartAPI();
    this._notifier = new CartNotifier();
    this._mutationQueue = Promise.resolve();
  }

  on(event, cb)  { return this._notifier.on(event, cb); }
  off(event, cb) { this._notifier.off(event, cb); }

  /**
   * Serializa mutações para evitar que respostas concorrentes sobrescrevam
   * o estado mais recente do carrinho da Shopify.
   */
  _enqueueMutation(operation) {
    const result = this._mutationQueue.then(operation, operation);
    this._mutationQueue = result.catch(() => undefined);
    return result;
  }

  /**
   * Adiciona um item ao carrinho.
   * @param {string|number} variantId
   * @param {{ quantity?: number, properties?: object, button?: HTMLElement }} options
   */
  async add(variantId, { quantity = 1, properties = {}, button, action = 'open_drawer' } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._enqueueMutation(
        () => this._api.add(variantId, quantity, properties)
      );
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('added', { variantId, quantity, data, action });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'add', error: error.message, variantId });
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove um item do carrinho.
   * @param {string} lineItemKey
   * @param {{ button?: HTMLElement, action?: string }} options
   */
  async remove(lineItemKey, { button, action = 'open_drawer' } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._enqueueMutation(
        () => this._api.remove(lineItemKey)
      );
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('removed', { lineItemKey, data, action });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'remove', error: error.message, lineItemKey });
      return { success: false, error: error.message };
    }
  }

  /**
   * Atualiza a quantidade de um item.
   * @param {string} lineItemKey
   * @param {number} quantity
   * @param {{ button?: HTMLElement }} options
   */
  async update(lineItemKey, quantity, { button } = {}) {
    if (button) window.ButtonFeedback?.loading(button);
    try {
      const data = await this._enqueueMutation(
        () => this._api.update(lineItemKey, quantity)
      );
      if (button) window.ButtonFeedback?.success(button);
      this._notifier.emit('updated', { lineItemKey, quantity, data });
      return { success: true, data };
    } catch (error) {
      if (button) window.ButtonFeedback?.error(button);
      this._notifier.emit('error', { action: 'update', error: error.message, lineItemKey });
      return { success: false, error: error.message };
    }
  }

  /** Retorna o estado atual do carrinho. */
  async get() {
    try {
      await this._mutationQueue;
      const data = await this._api.get();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Limpa o carrinho. */
  async clear() {
    try {
      const data = await this._enqueueMutation(() => this._api.clear());
      this._notifier.emit('cleared', { data });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

window.CartManager = new CartManager();
